import type { CSSProperties } from 'react';

import { useIntl } from 'react-intl';

import { wacToken } from '@/libraries/design/foundations';

import { METER_CONTENT } from '../content/meter-content';
import { useSelector } from '../context/hooks';
import { selectMeter } from '../context/selectors';

import type { Meter } from '../context/types';

/**
 * Lowercase / mid-sentence variants of the meter names.
 *
 * Used when the meter name appears inside a sentence (e.g. "how your
 * teams might use rovo credits"). The Confluence spec's meter names are
 * all already sentence-case-friendly ("Rovo credits", "Automation
 * steps", etc.), so the lowercase form is a lower-first variant of the
 * display label — except for `csResolutions` which uses the exact spec
 * form "AI agent resolutions" (the "AI" acronym stays uppercase).
 */
export const METER_LABELS_LOWER: Record<Meter, string> = {
	rovoCredits: 'rovo credits',
	automations: 'automation steps',
	csResolutions: 'AI agent resolutions',
	assetsObjects: 'assets objects',
};

/**
 * "How your teams might use {meter}" value-cards section — value cards
 * without icons. Shared by both the Estimate step (Step 3) and the
 * Summary step (Step 5) so the two screens stay visually and copy-wise
 * identical (single source of truth — GWP-12821).
 *
 * Renders a meter-aware eyebrow + a stack of bold-title / description
 * value-prop pairs sourced from the atomic content registry
 * (`src/content/meter-content.ts`). All copy is localized via
 * `react-intl`; message IDs are keyed by meter + index so translators
 * can localize per-meter copy without affecting other meters.
 */
export const HowYourTeamsMightUse = () => {
	const intl = useIntl();
	const meter = useSelector(selectMeter);

	const valueProps = METER_CONTENT[meter].valueProps.map((vp, idx) => ({
		title: intl.formatMessage({
			id: `ubp.calculator.estimate.${meter}.valueProps.${idx}.title`,
			defaultMessage: vp.title,
		}),
		description: intl.formatMessage({
			id: `ubp.calculator.estimate.${meter}.valueProps.${idx}.description`,
			defaultMessage: vp.description,
		}),
	}));

	return (
		<div style={howYourTeamsMightUseStyle}>
			{/*
			 * Meter-aware eyebrow — sourced from the atomic content
			 * registry so the copy reflects the customer-facing meter
			 * name (e.g. "How your teams might use rovo credits"). The
			 * message ID is scoped per meter so translators can localize
			 * the whole sentence at once (some locales don't preserve
			 * the "verb + meter noun" word order).
			 */}
			<p style={howYourTeamsMightUseEyebrowStyle}>
				{intl.formatMessage({
					id: `ubp.calculator.estimate.${meter}.howYourTeamsMightUse.eyebrow`,
					defaultMessage: `How your teams might use ${METER_LABELS_LOWER[meter]}`,
				})}
			</p>
			<div style={valuePropStackStyle}>
				{valueProps.map((vp, i) => (
					<div key={`${vp.title}-${i}`} style={valuePropItemStyle}>
						<p style={valuePropTitleStyle}>{vp.title}</p>
						<p style={valuePropDescriptionStyle}>{vp.description}</p>
					</div>
				))}
			</div>
		</div>
	);
};

// "How your teams might use" section — value cards without icons.
// Neutral-light-100 panel (matches ResultsCard) with 20px inside padding
// and 20px rounded corners. Content is a stack of bold title + description
// pairs with 24px between items (matches Figma 1103:32350).

const howYourTeamsMightUseStyle: CSSProperties = {
	backgroundColor: wacToken('color.neutral.light100'),
	borderRadius: '20px',
	padding: '20px 20px 32px 20px',
	display: 'flex',
	flexDirection: 'column',
	gap: '24px',
};

const howYourTeamsMightUseEyebrowStyle: CSSProperties = {
	margin: 0,
	fontFamily: wacToken('font.charlie.text'),
	fontSize: '14px',
	lineHeight: '18px',
	fontWeight: 400,
	textTransform: 'uppercase',
	letterSpacing: 0,
	color: wacToken('color.neutral.light1100'),
};

const valuePropStackStyle: CSSProperties = {
	display: 'flex',
	flexDirection: 'column',
	gap: '24px',
};

const valuePropItemStyle: CSSProperties = {
	display: 'flex',
	flexDirection: 'column',
	gap: 0,
};

const valuePropTitleStyle: CSSProperties = {
	margin: 0,
	fontFamily: wacToken('font.charlie.text'),
	fontSize: '16px',
	lineHeight: '24px',
	fontWeight: 700,
	color: wacToken('color.neutral.light1100'),
};

const valuePropDescriptionStyle: CSSProperties = {
	margin: 0,
	fontFamily: wacToken('font.charlie.text'),
	fontSize: '16px',
	lineHeight: '24px',
	fontWeight: 400,
	color: wacToken('color.neutral.light1100'),
};
